import { forwardRef, useEffect, useRef, useState } from "react";
import { GRAPHQL_ENDPOINT } from "src/constants/endpoints";
import { CREATE_PROMOTION_CODE } from "src/constants/mutations";
import { GET_PROMOTIONS } from "src/constants/queries";
import { useAuth } from "src/contexts/auth-provider";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { BtnLoader } from "src/Loader";
import { formatDate } from "src/utils/date";
import { failureToast, successToast } from "src/utils/toasts";
import useOutsideClick from "src/hooks/useOutsideClick";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "src/features/Events/date-picker.css";

type PromotionOption = {
  correlationId: string;
  name: string;
  status: string;
};

type CreatedCode = {
  correlationId: string;
  promotionName: string;
  code: string;
  status: string;
  expiresAt?: number | null;
};

const PromotionDropdown = ({
  value,
  options,
  onChange,
}: {
  value: string;
  options: PromotionOption[];
  onChange: (next: string) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOutsideClick({ ref, handler: () => setOpen(false) });
  const selected = options.find((option) => option.correlationId === value);

  return (
    <div className="relative text-left md:col-span-2" ref={ref}>
      <button
        type="button"
        className="inline-flex h-[40px] w-full items-center justify-between gap-x-1.5 whitespace-nowrap rounded-md border border-[#E5E7EB] bg-white px-3 py-2 text-sm text-[#111827] hover:border-[#d1d5db] hover:bg-[#fafafa] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FD7657]/40 focus-visible:border-[#FD7657] transition-colors"
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Select promotion"
        onClick={() => setOpen((prev) => !prev)}
      >
        <span>{selected ? `${selected.name} (${selected.status})` : "Select promotion"}</span>
        <svg
          className="-mr-1 h-5 w-5 text-[#9ca3af]"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>
      {open ? (
        <div
          className="absolute right-0 z-[100] mt-2 w-full origin-top-right rounded-md border border-[#E5E7EB] bg-white shadow-lg focus:outline-none"
          role="menu"
          aria-orientation="vertical"
        >
          <div className="py-1" role="none">
            {options.map((option) => {
              const isSelected = option.correlationId === value;
              return (
                <button
                  key={option.correlationId}
                  type="button"
                  className={`block w-full px-4 py-2 text-left text-sm ${
                    isSelected
                      ? "bg-[#fef3ef] font-medium text-[#111827]"
                      : "text-[#374151] hover:bg-[#f9fafb]"
                  }`}
                  role="menuitem"
                  onClick={() => {
                    onChange(option.correlationId);
                    setOpen(false);
                  }}
                >
                  {option.name} ({option.status})
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export const CreatePromotion = () => {
  const { hasRestaurant } = useAuth();
  const { axBe } = useAxiosInterceptor();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [promotions, setPromotions] = useState<PromotionOption[]>([]);
  const [recentCodes, setRecentCodes] = useState<CreatedCode[]>([]);
  const [selectedPromotionId, setSelectedPromotionId] = useState("");
  const [code, setCode] = useState("");
  const [startsAt, setStartsAt] = useState<Date | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [maxRedemptionsTotal, setMaxRedemptionsTotal] = useState("");
  const [maxRedemptionsPerCustomer, setMaxRedemptionsPerCustomer] = useState("");
  const DateInput = forwardRef<HTMLInputElement, { value?: string; onClick?: () => void; placeholder?: string }>(
    ({ value, onClick, placeholder }, ref) => (
      <input
        type="text"
        className="h-[40px] w-full rounded-lg border border-[#F1F1F2] px-3 text-sm"
        value={value || ""}
        onClick={onClick}
        ref={ref}
        placeholder={placeholder}
        readOnly
      />
    )
  );

  useEffect(() => {
    const loadPromotions = async () => {
      if (!hasRestaurant?.correlationId) return;
      setLoading(true);
      try {
        const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
          query: GET_PROMOTIONS,
          variables: { storeCorrelationId: hasRestaurant.correlationId },
        });
        const active = (data?.data?.promotionsByStore || []).filter(
          (promotion: PromotionOption) =>
            promotion.status === "ACTIVE" || promotion.status === "DRAFT"
        );
        setPromotions(active);
        if (active.length && !selectedPromotionId) {
          setSelectedPromotionId(active[0].correlationId);
        }
      } catch {
        failureToast("Failed to load promotions.");
      } finally {
        setLoading(false);
      }
    };
    loadPromotions();
  }, [axBe, hasRestaurant?.correlationId, selectedPromotionId]);

  const selectedPromotionName =
    promotions.find((promotion) => promotion.correlationId === selectedPromotionId)
      ?.name || "Promotion";

  const submitCode = async () => {
    if (!hasRestaurant?.correlationId || !selectedPromotionId) {
      failureToast("Select a promotion first.");
      return;
    }
    if (!code.trim()) {
      failureToast("Promotion code is required.");
      return;
    }

    setSubmitting(true);
    try {
      const { data } = await axBe.post(GRAPHQL_ENDPOINT, {
        query: CREATE_PROMOTION_CODE,
        variables: {
          input: {
            storeCorrelationId: hasRestaurant.correlationId,
            promotionCorrelationId: selectedPromotionId,
            code: code.trim(),
            startsAt: startsAt ? startsAt.getTime() : null,
            expiresAt: expiresAt ? expiresAt.getTime() : null,
            maxRedemptionsTotal: maxRedemptionsTotal
              ? Number(maxRedemptionsTotal)
              : null,
            maxRedemptionsPerCustomer: maxRedemptionsPerCustomer
              ? Number(maxRedemptionsPerCustomer)
              : null,
          },
        },
      });

      const created = data?.data?.createPromotionCode;
      if (created?.correlationId) {
        setRecentCodes((prev) => [
          {
            correlationId: created.correlationId,
            promotionName: selectedPromotionName,
            code: created.code,
            status: created.status,
            expiresAt: created.expiresAt,
          },
          ...prev,
        ]);
        setCode("");
        setStartsAt(null);
        setExpiresAt(null);
        setMaxRedemptionsTotal("");
        setMaxRedemptionsPerCustomer("");
        successToast("Promotion code created.");
      }
    } catch {
      failureToast("Unable to create promotion code.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-[#252525]">Create promotion code</h3>
        {loading ? (
          <div className="py-4">
            <BtnLoader />
          </div>
        ) : (
          <>
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <PromotionDropdown
                value={selectedPromotionId}
                options={promotions}
                onChange={setSelectedPromotionId}
              />
              <input
                className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm md:col-span-2"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="Promotion code, e.g. SAVE10"
              />
              <DatePicker
                selected={startsAt}
                onChange={(date) => setStartsAt(date)}
                showTimeSelect
                dateFormat="MM/dd/yyyy h:mm aa"
                customInput={<DateInput placeholder="Start date and time" />}
                className="w-full"
              />
              <DatePicker
                selected={expiresAt}
                onChange={(date) => setExpiresAt(date)}
                showTimeSelect
                dateFormat="MM/dd/yyyy h:mm aa"
                customInput={<DateInput placeholder="Expiry date and time" />}
                className="w-full"
                minDate={startsAt || undefined}
              />
              <input
                type="number"
                className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm"
                value={maxRedemptionsTotal}
                onChange={(e) => setMaxRedemptionsTotal(e.target.value)}
                placeholder="Max total redemptions"
              />
              <input
                type="number"
                className="h-[40px] rounded-lg border border-[#F1F1F2] px-3 text-sm"
                value={maxRedemptionsPerCustomer}
                onChange={(e) => setMaxRedemptionsPerCustomer(e.target.value)}
                placeholder="Max per customer"
              />
            </div>
            <div className="mt-3 flex justify-end">
              {submitting ? (
                <BtnLoader />
              ) : (
                <button
                  className="h-[38px] rounded-lg bg-orange px-4 text-sm text-white"
                  onClick={submitCode}
                >
                  Create code
                </button>
              )}
            </div>
          </>
        )}
      </div>

      <div className="rounded-xl bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-[#252525]">Recently created codes</h3>
        {recentCodes.length === 0 ? (
          <p className="py-4 text-sm text-[#9B9B9B]">
            No codes created in this session yet.
          </p>
        ) : (
          <div className="mt-3 overflow-x-auto">
            <table className="min-w-[620px] w-full text-left text-sm">
              <thead className="border-b border-borderGray text-xs text-[#9B9B9B]">
                <tr>
                  <th className="px-3 py-3">Promotion</th>
                  <th className="px-3 py-3">Code</th>
                  <th className="px-3 py-3">Status</th>
                  <th className="px-3 py-3">Expires</th>
                </tr>
              </thead>
              <tbody>
                {recentCodes.map((recentCode) => (
                  <tr key={recentCode.correlationId} className="border-b border-[#EBF5FF]">
                    <td className="px-3 py-3">{recentCode.promotionName}</td>
                    <td className="px-3 py-3 font-medium">{recentCode.code}</td>
                    <td className="px-3 py-3">{recentCode.status}</td>
                    <td className="px-3 py-3">
                      {recentCode.expiresAt ? formatDate(recentCode.expiresAt) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
